///
/// EarthBrowser
///
/// Created by Matt Giger on 10/29/2026
/// Copyright (c) 2026 Geodata Labs
///
/// One tooltip for every `title` attribute in the app. Mounted once
/// (PageContent); authors keep writing `title="Pause (Space)"` and get the
/// HUD glass tooltip instead of the OS one: consistent 450 ms delay with
/// instant re-show while moving between neighbours, a keycap for a
/// trailing "(Space)" / "(,)" shortcut, keyboard focus support, and a
/// long-press on touch (native titles never show on phones).
///
/// On first hover/focus the element's `title` moves to `data-title` (so
/// the browser's tooltip is suppressed without losing the text) and, if
/// the element has no other description, to `aria-description`, so
/// assistive tech keeps the hint. Opt an element out with
/// `data-native-title`.
///

import { cn } from "@/utils/utils"
import { useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"

const cShowDelayMs = 750
/** Re-show instantly if another tooltip was hidden this recently (tooltip group feel) */
const cWarmWindowMs = 350
const cLongPressMs = 500
const cTouchHoldMs = 1800
const cGapPx = 8
const cMarginPx = 8

const cKeyNames = new Set([
	"space", "esc", "escape", "enter", "return", "tab", "home", "end", "delete", "backspace",
	"up", "down", "left", "right", "↑", "↓", "←", "→",
])

/** Split "Pause (Space)" into body + keycap; leaves prose parentheticals alone */
function splitShortcut(text: string): { body: string; key?: string } {
	const match = /^(.*?)\s*\(([^()]{1,16})\)$/.exec(text.trim())
	if (!match) return { body: text }
	const key = match[2].trim()
	const looksLikeKey = key.length === 1
		|| cKeyNames.has(key.toLowerCase())
		|| /[⌘⇧⌥⌃]|\bctrl\b|\bcmd\b|\bshift\b|\balt\b|\boption\b|\+/i.test(key)
	return looksLikeKey ? { body: match[1].trim(), key } : { body: text }
}

interface TooltipState {
	text: string
	anchor: DOMRect
}

function findTitled(node: EventTarget | null): HTMLElement | null {
	if (!(node instanceof Element)) return null
	const el = node.closest<HTMLElement>("[title], [data-title]")
	if (!el || el.hasAttribute("data-native-title")) return null
	return el
}

/** Move `title` to `data-title` (and `aria-description`); returns the hint text */
function adoptTitle(el: HTMLElement): string {
	const title = el.getAttribute("title")
	if (title !== null) {
		el.setAttribute("data-title", title)
		el.removeAttribute("title")
		if (!el.hasAttribute("aria-description") && !el.hasAttribute("aria-describedby")) {
			el.setAttribute("aria-description", title)
		}
	}
	return el.getAttribute("data-title") ?? ""
}

export function TitleTooltipLayer() {
	const [state, setState] = useState<TooltipState | null>(null)

	useEffect(() => {
		let active: HTMLElement | null = null
		let suppressed: HTMLElement | null = null
		let showTimer = 0
		let hideTimer = 0
		let longPressTimer = 0
		let visible = false
		let lastHiddenAt = 0
		let touchShown = false
		// React re-applies `title` when the prop changes while hovered
		// (play → pause); re-adopt it and refresh the text in place
		const observer = new MutationObserver(() => {
			if (!active) return
			const text = adoptTitle(active)
			if (visible && text) {
				setState({ text, anchor: active.getBoundingClientRect() })
			}
		})

		const clearTimers = () => {
			window.clearTimeout(showTimer)
			window.clearTimeout(hideTimer)
			showTimer = 0
			hideTimer = 0
		}

		const hide = () => {
			clearTimers()
			if (visible) lastHiddenAt = performance.now()
			visible = false
			touchShown = false
			setState(null)
		}

		const release = () => {
			hide()
			observer.disconnect()
			active = null
		}

		const show = () => {
			if (!active || active === suppressed || !active.isConnected) return
			const text = adoptTitle(active)
			if (!text) return
			visible = true
			setState({ text, anchor: active.getBoundingClientRect() })
		}

		const engage = (el: HTMLElement, delay: number) => {
			if (el === active) return
			release()
			active = el
			observer.observe(el, { attributes: true, attributeFilter: ["title"] })
			const text = adoptTitle(el)
			if (!text) return
			if (delay <= 0) {
				show()
			} else {
				showTimer = window.setTimeout(show, delay)
			}
		}

		const onPointerOver = (e: PointerEvent) => {
			if (e.pointerType === "touch") return
			const el = findTitled(e.target)
			if (!el) return
			if (el !== suppressed) suppressed = null
			const warm = visible || performance.now() - lastHiddenAt < cWarmWindowMs
			engage(el, warm ? 0 : cShowDelayMs)
		}

		const onPointerOut = (e: PointerEvent) => {
			if (!active || e.pointerType === "touch") return
			const to = e.relatedTarget
			if (to instanceof Node && active.contains(to)) return
			release()
		}

		const onPointerDown = (e: PointerEvent) => {
			if (e.pointerType === "touch") {
				const el = findTitled(e.target)
				window.clearTimeout(longPressTimer)
				if (!el) return
				longPressTimer = window.setTimeout(() => {
					engage(el, 0)
					touchShown = true
				}, cLongPressMs)
				return
			}
			// A click means the hint did its job; stay quiet until the pointer leaves
			suppressed = active
			hide()
		}

		const onPointerUp = () => {
			window.clearTimeout(longPressTimer)
			if (touchShown) {
				hideTimer = window.setTimeout(release, cTouchHoldMs)
			}
		}

		const onFocusIn = (e: FocusEvent) => {
			const el = findTitled(e.target)
			if (!el?.matches(":focus-visible")) return
			engage(el, 0)
		}

		const onFocusOut = (e: FocusEvent) => {
			if (active && e.target === active) release()
		}

		const onKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") release()
		}

		const onScrollOrResize = () => {
			if (visible) release()
		}

		document.addEventListener("pointerover", onPointerOver, true)
		document.addEventListener("pointerout", onPointerOut, true)
		document.addEventListener("pointerdown", onPointerDown, true)
		document.addEventListener("pointerup", onPointerUp, true)
		document.addEventListener("pointercancel", onPointerUp, true)
		document.addEventListener("focusin", onFocusIn, true)
		document.addEventListener("focusout", onFocusOut, true)
		document.addEventListener("keydown", onKeyDown, true)
		document.addEventListener("scroll", onScrollOrResize, true)
		window.addEventListener("resize", onScrollOrResize)
		return () => {
			release()
			window.clearTimeout(longPressTimer)
			document.removeEventListener("pointerover", onPointerOver, true)
			document.removeEventListener("pointerout", onPointerOut, true)
			document.removeEventListener("pointerdown", onPointerDown, true)
			document.removeEventListener("pointerup", onPointerUp, true)
			document.removeEventListener("pointercancel", onPointerUp, true)
			document.removeEventListener("focusin", onFocusIn, true)
			document.removeEventListener("focusout", onFocusOut, true)
			document.removeEventListener("keydown", onKeyDown, true)
			document.removeEventListener("scroll", onScrollOrResize, true)
			window.removeEventListener("resize", onScrollOrResize)
		}
	}, [])

	// Two commits, but the visible bubble exists only at its final spot:
	// the hidden twin measures the text, then the bubble mounts already
	// positioned. Nothing moves and nothing animates, so there is no
	// frame in which Chrome can show it travelling from an old position
	const measureRef = useRef<HTMLDivElement>(null)
	const [placed, setPlaced] = useState<(TooltipState & { left: number; top: number }) | null>(null)
	useLayoutEffect(() => {
		if (!state) {
			setPlaced(null)
			return
		}
		const el = measureRef.current
		if (!el) return
		const w = el.offsetWidth
		const h = el.offsetHeight
		const vw = window.innerWidth
		const vh = window.innerHeight
		const { anchor } = state
		let top = anchor.top - h - cGapPx
		if (top < cMarginPx) {
			top = Math.min(anchor.bottom + cGapPx, vh - h - cMarginPx)
		}
		const left = Math.max(cMarginPx, Math.min(anchor.left + anchor.width / 2 - w / 2, vw - w - cMarginPx))
		setPlaced({ ...state, left, top })
	}, [state])

	return createPortal(
		<>
			{state && (
				<div ref={measureRef} aria-hidden="true" className={cn(cBubbleClass, "invisible")} style={{ left: -9999, top: -9999 }}>
					<BubbleContent text={state.text} />
				</div>
			)}
			{placed && (
				<div role="tooltip" className={cBubbleClass} style={{ left: placed.left, top: placed.top }}>
					<BubbleContent text={placed.text} />
				</div>
			)}
		</>,
		document.body
	)
}

// Same shell as TooltipContent / the HUD chrome. Deliberately no
// enter animation: a tooltip appears, it doesn't arrive
const cBubbleClass = "fixed z-[9500] pointer-events-none max-w-[260px] rounded-xl border border-white/15 bg-black/70 backdrop-blur-xl px-2.5 py-1.5 text-xs leading-snug text-white/90 shadow-xl"

function BubbleContent({ text }: { text: string }) {
	const { body, key } = splitShortcut(text)
	return (
		<>
			{body}
			{key && (
				<kbd className="ml-1.5 inline-block align-[1px] rounded border border-white/20 bg-white/10 px-1 py-px font-mono text-[10px] leading-none text-white/80">
					{key}
				</kbd>
			)}
		</>
	)
}
